import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

/**
 * "Report an issue" on an item of the profile slide-out — a trait (CT-5715), an
 * insight (CT-5716). Each kind passes its own call and copy; the rules live here
 * once:
 *
 * - It submits directly (CT-5715, open question 2) and says how it went in a toast.
 * - A double click reports once: the ref flips before any state update lands, so
 *   the second click is already refused.
 * - A success is final for this item: `reported` stays true and the ref stays
 *   closed, so the button does not come back while the item is re-read — nor if
 *   that re-read fails. Only a failed report opens it again. The slide-out
 *   mounts once per item, so nothing carries over to the next one.
 *
 * @param {object} options
 * @param {() => Promise<unknown>} options.send — the report request; stable (`useCallback`).
 * @param {{ successTitle: string, successMessage: string, errorTitle: string, errorMessage: string }} options.toastKeys
 *   — i18n keys of the toasts; a module-level constant.
 * @param {() => void} [options.onReported] — re-read the item, quietly.
 * @param {() => void} [options.onChanged] — tell the page the item changed (How
 *   to approach reads from it, CT-5714).
 */
export function useReportItemIssue({ send, toastKeys, onReported, onChanged } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const submittingRef = useRef(false);

  const report = useCallback(async () => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setReporting(true);

    try {
      await send();
      setReported(true);
      toast.success({ title: t(toastKeys.successTitle), message: t(toastKeys.successMessage) });
      onReported?.();
      onChanged?.();
      return true;
    } catch (err) {
      submittingRef.current = false;
      toast.error({ title: t(toastKeys.errorTitle), message: err?.message ?? t(toastKeys.errorMessage) });
      return false;
    } finally {
      setReporting(false);
    }
  }, [send, toastKeys, onReported, onChanged, toast, t]);

  return useMemo(() => ({ report, reporting, reported }), [report, reporting, reported]);
}

export default useReportItemIssue;
